import { z } from "zod";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ColumnDef } from "@tanstack/react-table";
import { AlertTriangle, X, Plus, Check, Search } from "lucide-react";

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Form } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

import {
  FormInput,
  FormDatePickerV2,
  FormCheckbox,
  FormSelect
} from "@/components/forms";
import { FormComboboxForeignKey } from "./form-combobox-foreign-key";
import { SimpleTable } from "./simple-table";

import { capitalize, cn, lowercaseFirst } from "@/lib/utils";
import { isDateTimeWithClockField } from "@/lib/pps-date-time";
import { JobOrderAttributeDataType } from "../list/constant";
import {
  mapPartProcessesToRows,
  ProcessSelectionRow,
  SelectedProcessOverview
} from "./workflow-process-selection";
import { GetPartProcesses } from "@/hooks/services/pps/handlers/part-process-api";

interface AddJobOrderSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attributes: any[];
  onSubmit: (
    data: any,
    setOpen: (value: boolean) => void,
    form: any,
    setSelectedProcesses: (value: any[]) => void,
    selectedProcesses: any[]
  ) => void;
  isLoading?: boolean;
}

export function AddJobOrderSheet({
  open,
  onOpenChange,
  attributes,
  onSubmit,
  isLoading = false
}: AddJobOrderSheetProps) {
  const [selectedProcesses, setSelectedProcesses] = useState<
    ProcessSelectionRow[]
  >([]);
  const [processSearchTerm, setProcessSearchTerm] = useState("");
  const [showProcessSelection, setShowProcessSelection] = useState(false);
  const [selectedPartId, setSelectedPartId] = useState<number | null>(null);
  const previousSelectedPartIdRef = useRef<number | null>(null);

  const getManualFlowPartProcesses = (params: any) => {
    const stringParams = JSON.stringify(params);

    return {
      name: `GetManualFlowPartProcesses_${selectedPartId}_${stringParams}`,
      handle: async () => {
        const { page, size, ...restParams } = params;
        const response = await GetPartProcesses({
          ...restParams,
          partId: selectedPartId,
          page,
          pageSize: size
        }).handle();

        const mappedData = mapPartProcessesToRows(response?.data || []);

        return {
          data: mappedData,
          totalItems: response?.total ?? mappedData.length
        };
      }
    };
  };

  // Define columns for SimpleTable
  const processColumns: ColumnDef<ProcessSelectionRow>[] = [
    {
      id: "select",
      header: () => <div className="aero:w-12" />,
      cell: ({ row }) => {
        const isChecked = selectedProcesses.some(
          (p) => p.partProcessId === row.original.partProcessId
        );
        return (
          <div className="aero:flex aero:items-center aero:justify-center">
            <Checkbox
              checked={isChecked}
              onCheckedChange={(checked) => {
                if (checked) {
                  setSelectedProcesses((prev) => {
                    const exists = prev.find(
                      (p) => p.partProcessId === row.original.partProcessId
                    );
                    if (!exists) {
                      return [...prev, row.original];
                    }
                    return prev;
                  });
                } else {
                  setSelectedProcesses((prev) =>
                    prev.filter(
                      (p) => p.partProcessId !== row.original.partProcessId
                    )
                  );
                }
              }}
            />
          </div>
        );
      },
      size: 48
    },
    {
      accessorKey: "processName",
      header: () => (
        <span className="aero:text-sm aero:font-bold aero:text-neutral-900">
          Process Name
        </span>
      ),
      cell: ({ row }) => {
        const selectedIndex = selectedProcesses.findIndex(
          (process) => process.partProcessId === row.original.partProcessId
        );

        return (
          <div className="aero:flex aero:items-center aero:gap-2">
            <div className="aero:text-sm aero:text-neutral-900">
              <span className="aero:font-medium">{row.original.processNo}</span>
              <span> - {row.original.processName}</span>
            </div>
            {selectedIndex >= 0 && (
              <span className="aero:inline-flex aero:items-center aero:rounded-full aero:border aero:border-neutral-400 aero:px-2 aero:py-0.5 aero:text-[11px] aero:font-semibold aero:leading-none aero:text-neutral-700">
                Step {selectedIndex + 1}
              </span>
            )}
          </div>
        );
      },
      size: 280
    },
    {
      accessorKey: "shopName",
      header: () => (
        <span className="aero:text-sm aero:font-bold aero:text-neutral-900">
          Shop Name
        </span>
      ),
      cell: ({ row }) => (
        <span className="aero:text-sm aero:text-neutral-900">
          {row.original.shopName}
        </span>
      ),
      size: 140
    },
    {
      accessorKey: "processStandardHour",
      header: () => (
        <span className="aero:text-sm aero:font-bold aero:text-neutral-900">
          Standard Hour
        </span>
      ),
      cell: ({ row }) => (
        <span className="aero:text-sm aero:text-neutral-900">
          {typeof row.original.processStandardHour === "number"
            ? row.original.processStandardHour
            : "-"}
        </span>
      ),
      size: 120
    }
  ];

  // Build dynamic Zod schema based on attributes
  const textSchema = z.coerce
    .string()
    .max(500, "Text must be less than 500 characters");

  const schema =
    attributes && attributes.length > 0
      ? attributes.reduce(
          (acc, attribute) => {
            let validator: z.ZodType;

            switch (attribute.dataType) {
              case JobOrderAttributeDataType.FOREIGN_KEY:
                validator = z.string();
                break;
              case JobOrderAttributeDataType.INT:
                validator = z.coerce
                  .number()
                  .int()
                  .min(0, `${attribute.label} must be a positive integer`);
                break;
              case JobOrderAttributeDataType.FRACTION:
                validator = z.coerce
                  .number()
                  .min(0, `${attribute.label} must be a positive number`);
                break;
              case JobOrderAttributeDataType.BOOLEAN:
                validator = z
                  .boolean()
                  .optional()
                  .or(z.literal("true").transform(() => true))
                  .or(z.literal("false").transform(() => false));
                break;
              case JobOrderAttributeDataType.DATETIME:
                validator = z
                  .string()
                  .nullable()
                  .refine((val) => !val || !isNaN(Date.parse(val)), {
                    message: `${attribute.label} must be a valid date`
                  });
                break;
              case JobOrderAttributeDataType.TEXT:
                validator = textSchema;
                break;
              default:
                validator = textSchema;
                break;
            }

            acc[attribute.name] = attribute.isRequired
              ? validator.refine(
                  (val) => val !== undefined && val !== null && val !== "",
                  {
                    message: `${attribute.label} is required`
                  }
                )
              : validator.optional();

            return acc;
          },
          {} as Record<string, z.ZodType>
        )
      : {};

  const zodSchema = z.object({
    ...schema,
    process: z.string().min(1, "Process is required")
  });

  const form = useForm({
    resolver: zodResolver(zodSchema),
    defaultValues: {
      process: "0"
    }
  });

  const selectedPartValue = form.watch("part");

  useEffect(() => {
    const parsedPartId = Number(selectedPartValue);
    const nextPartId =
      Number.isFinite(parsedPartId) && parsedPartId > 0 ? parsedPartId : null;

    setSelectedPartId(nextPartId);
  }, [selectedPartValue]);

  useEffect(() => {
    const previousPartId = previousSelectedPartIdRef.current;

    if (previousPartId === null) {
      previousSelectedPartIdRef.current = selectedPartId;
      return;
    }

    if (previousPartId !== selectedPartId) {
      setSelectedProcesses([]);
      setProcessSearchTerm("");
      form.setValue("process", "0");
      form.clearErrors("process");
    }

    previousSelectedPartIdRef.current = selectedPartId;
  }, [form, selectedPartId]);

  useEffect(() => {
    const currentProcessValue = form.getValues("process");
    if (!currentProcessValue || currentProcessValue === "0") {
      return;
    }

    const isCurrentProcessValid = selectedProcesses.some(
      (process) => process.processId.toString() === currentProcessValue
    );

    if (!isCurrentProcessValid) {
      form.setValue("process", "0");
      form.clearErrors("process");
    }
  }, [form, selectedProcesses]);

  const isStaticField = (attr: any) => {
    return attr.isSystemAttribute === true;
  };

  const staticAttributeOrder: Record<string, number> = {
    jobOrderNo: 0,
    mainJobOrder: 1,
    part: 2,
    serialNumber: 3,
    customer: 4,
    dateIn: 5,
    edd: 6,
    hoursClocked: 7
  };

  const staticAttributes = attributes
    .filter((attr) => isStaticField(attr))
    .sort(
      (a, b) =>
        (staticAttributeOrder[a.name] ?? 99) -
        (staticAttributeOrder[b.name] ?? 99)
    );
  const dynamicAttributes = attributes.filter((attr) => !isStaticField(attr));

  const resetFormToEmpty = () => {
    const emptyValues: Record<string, any> = {
      process: "0"
    };

    attributes.forEach((attr) => {
      switch (attr.dataType) {
        case JobOrderAttributeDataType.BOOLEAN:
          emptyValues[attr.name] = false;
          break;
        case JobOrderAttributeDataType.INT:
        case JobOrderAttributeDataType.FRACTION:
          emptyValues[attr.name] = undefined;
          break;
        case JobOrderAttributeDataType.FOREIGN_KEY:
        case JobOrderAttributeDataType.DATETIME:
          emptyValues[attr.name] = null;
          break;
        default:
          emptyValues[attr.name] = "";
          break;
      }
    });

    form.reset(emptyValues);
  };

  const handleBackToForm = () => {
    setShowProcessSelection(false);
    setProcessSearchTerm("");
  };

  const handleClose = () => {
    onOpenChange(false);
    setSelectedProcesses([]);
    setShowProcessSelection(false);
    setProcessSearchTerm("");
    setSelectedPartId(null);
    previousSelectedPartIdRef.current = null;
    resetFormToEmpty();
  };

  const handleSubmit = (data: any) => {
    form.clearErrors("process");

    if (selectedProcesses.length === 0 && data.process !== "0") {
      form.setError("process", {
        type: "manual",
        message:
          "Current Process must be Not Started when no process steps are selected."
      });
      return;
    }

    if (data.process !== "0") {
      const selectedProcessIds = selectedProcesses.map((process) =>
        process.processId.toString()
      );

      if (!selectedProcessIds.includes(data.process)) {
        form.setError("process", {
          type: "manual",
          message: "Current Process must be one of the selected process steps."
        });
        return;
      }
    }

    onSubmit(
      data,
      onOpenChange,
      resetFormToEmpty,
      setSelectedProcesses,
      selectedProcesses
    );
  };

  const renderField = (attribute: any) => {
    const commonProps = {
      key: attribute.name,
      name: attribute.name,
      label: capitalize(attribute.label),
      control: form.control
    };

    switch (attribute.dataType) {
      case JobOrderAttributeDataType.FOREIGN_KEY: {
        return (
          <FormComboboxForeignKey
            {...commonProps}
            referenceTable={lowercaseFirst(attribute.referenceTable)}
            nestedColumnName={attribute.nestedColumnName}
          />
        );
      }

      case JobOrderAttributeDataType.INT:
        return (
          <FormInput
            {...commonProps}
            type="number"
            placeholder={`Enter ${attribute.label.toLowerCase()}`}
          />
        );
      case JobOrderAttributeDataType.DATETIME: {
        const isDateTimeWithClock = isDateTimeWithClockField(
          attribute.name,
          attribute.label
        );

        return (
          <FormDatePickerV2
            {...commonProps}
            showTime={isDateTimeWithClock}
            placeholder={
              isDateTimeWithClock
                ? `Select ${attribute.label.toLowerCase()} date and time`
                : `Select ${attribute.label.toLowerCase()}`
            }
          />
        );
      }
      case JobOrderAttributeDataType.BOOLEAN:
        return (
          <div className="aero:flex aero:items-center">
            <FormCheckbox {...commonProps} />
          </div>
        );
      default:
        return (
          <FormInput
            {...commonProps}
            placeholder={`Enter ${attribute.label.toLowerCase()}`}
          />
        );
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="aero:w-full aero:sm:max-w-[673px] aero:p-0 aero:gap-0 aero:flex aero:flex-col"
      >
        {/* Custom close button - hide default */}
        <style>{`
          [data-slot="sheet-content"] > button[class*="absolute"][class*="right-4"] {
            display: none;
          }
        `}</style>

        {/* Header */}
        <SheetHeader className="aero:px-6 aero:pt-6 aero:pb-3 aero:gap-3 aero:border-b aero:border-neutral-300">
          <div className="aero:flex aero:items-center aero:justify-between aero:h-12 aero:gap-3">
            <SheetTitle className="aero:font-bold aero:text-xl aero:leading-9 aero:text-neutral-900">
              Create New Job Order
            </SheetTitle>
            <SheetClose asChild>
              <button
                onClick={handleClose}
                className="aero:w-6 aero:h-6 aero:flex aero:items-center aero:justify-center aero:rounded aero:hover:bg-neutral-100"
              >
                <X className="aero:w-5 aero:h-5 aero:text-neutral-500" />
              </button>
            </SheetClose>
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="aero:flex-1 aero:overflow-y-auto aero:px-6 aero:py-4">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="aero:flex aero:flex-col aero:gap-5"
            >
              {!showProcessSelection ? (
                <>
                  {/* Section: Basic Information */}
                  <div className="aero:flex aero:flex-col aero:gap-5">
                    <div className="aero:flex aero:items-center aero:gap-2">
                      <div className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
                      <h3 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
                        Basic Information
                      </h3>
                    </div>

                    {/* Static Fields */}
                    <div className="aero:grid aero:grid-cols-2 aero:gap-4">
                      {staticAttributes.map(renderField)}
                    </div>
                  </div>

                  {/* Section: Processes */}
                  <div className="aero:flex aero:flex-col aero:gap-5">
                    <div className="aero:flex aero:items-center aero:justify-between aero:gap-3">
                      <div className="aero:flex aero:items-center aero:gap-2">
                        <div className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
                        <h3 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
                          Workflow Process Steps
                        </h3>
                      </div>

                      <Button
                        type="button"
                        intent="primary"
                        onClick={() => setShowProcessSelection(true)}
                        disabled={!selectedPartId}
                        className="aero:h-9 aero:gap-2 aero:px-4 aero:bg-[#193CB8] hover:aero:bg-[#1534A0] aero:text-white"
                      >
                        <Plus className="aero:h-4 aero:w-4" />
                        Add Process
                      </Button>
                    </div>

                    {selectedProcesses.length === 0 ? (
                      /* Empty State */
                      <div className="aero:flex aero:flex-col aero:items-center aero:justify-center aero:gap-1 aero:py-10 aero:px-6 aero:border aero:border-neutral-300 aero:rounded-md aero:bg-neutral-50">
                        <div className="aero:text-center">
                          <div className="aero:text-lg aero:font-semibold aero:text-neutral-900 aero:mb-1">
                            No Processes / Workflow
                          </div>
                          <div className="aero:text-sm aero:text-neutral-500">
                            Add processes to this job order
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Selected Processes View */
                      <SelectedProcessOverview
                        selectedProcesses={selectedProcesses}
                        mode="timeline"
                      />
                    )}

                    {!selectedPartId && (
                      <div className="aero:flex aero:items-start aero:gap-2 aero:rounded-md aero:border aero:border-amber-300 aero:bg-amber-50 aero:px-3 aero:py-2">
                        <AlertTriangle className="aero:mt-0.5 aero:h-4 aero:w-4 aero:shrink-0 aero:text-amber-700" />
                        <div className="aero:text-sm aero:leading-5 aero:text-amber-900">
                          <span className="aero:font-semibold">
                            Action required:
                          </span>{" "}
                          Select Part first to load Manual Flow Input steps.
                        </div>
                      </div>
                    )}

                    <div className="aero:flex aero:flex-col aero:gap-3">
                      <Label className="aero:!text-sm aero:!font-medium">
                        Current Process
                      </Label>
                      <FormSelect
                        name="process"
                        triggerClassName="aero:w-full"
                        placeholder="Select Process"
                        options={[
                          {
                            value: "0",
                            label: "Not Started"
                          },
                          ...selectedProcesses.map((m) => ({
                            value: m.processId.toString(),
                            label: `${m.processNo} - ${m.processName}`
                          }))
                        ]}
                        control={form.control}
                      />
                    </div>
                  </div>

                  {/* Section: Additional Information */}
                  {dynamicAttributes.length > 0 && (
                    <div className="aero:flex aero:flex-col aero:gap-5">
                      <div className="aero:flex aero:items-center aero:gap-2">
                        <div className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
                        <h3 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
                          Additional Information
                        </h3>
                      </div>

                      <div className="aero:grid aero:grid-cols-3 aero:gap-4">
                        {dynamicAttributes.map(renderField)}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* Process Selection View */
                <div className="aero:flex aero:flex-col aero:gap-5 aero:h-full">
                  <div className="aero:flex aero:items-center aero:gap-2">
                    <div className="aero:h-7 aero:w-[2px] aero:rounded-full aero:bg-brand-primary-600" />
                    <h3 className="aero:text-lg aero:font-bold aero:leading-7 aero:text-brand-primary-600">
                      Workflow Process Steps
                    </h3>
                  </div>

                  {/* Search Bar */}
                  <div className="aero:flex aero:flex-col aero:gap-2">
                    <Label className="aero:!text-sm aero:!font-bold">
                      Search By
                    </Label>
                    <div className="aero:relative">
                      <Search className="aero:absolute aero:left-3 aero:top-1/2 aero:-translate-y-1/2 aero:w-4 aero:h-4 aero:text-neutral-400" />
                      <Input
                        placeholder="Search by process name or shop..."
                        value={processSearchTerm}
                        onChange={(e) => setProcessSearchTerm(e.target.value)}
                        disabled={!selectedPartId}
                        className="aero:h-11 aero:pl-9"
                      />
                    </div>
                  </div>

                  {/* Selected Data Overview */}
                  <SelectedProcessOverview
                    selectedProcesses={selectedProcesses}
                    mode="editable"
                    onRemoveProcess={(partProcessId) =>
                      setSelectedProcesses((prev) =>
                        prev.filter(
                          (process) => process.partProcessId !== partProcessId
                        )
                      )
                    }
                  />

                  {/* Process Table */}
                  <div className="aero:flex-1 aero:flex aero:flex-col aero:rounded-md aero:border aero:border-neutral-300 aero:bg-white aero:overflow-hidden">
                    {selectedPartId ? (
                      <div className="aero:h-[340px] aero:min-h-[340px]">
                        <SimpleTable<ProcessSelectionRow>
                          api={getManualFlowPartProcesses}
                          filterParams={{ search: processSearchTerm }}
                          columns={processColumns}
                          defaultPageSize={10}
                        />
                      </div>
                    ) : (
                      <div className="aero:flex aero:flex-1 aero:items-center aero:justify-center aero:text-sm aero:text-neutral-500 aero:px-6 aero:text-center">
                        Select Part in Basic Information to load Manual Flow
                        Input process steps.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </form>
          </Form>
        </div>

        {/* Footer */}
        <SheetFooter className="aero:p-6 aero:pt-4 aero:border-t aero:border-neutral-300 aero:flex aero:flex-row aero:justify-end aero:gap-3">
          {!showProcessSelection && (
            <Button
              type="button"
              intent="neutral"
              design="outline"
              onClick={handleClose}
            >
              Cancel
            </Button>
          )}
          {!showProcessSelection && (
            <Button
              type="submit"
              intent="primary"
              onClick={form.handleSubmit(handleSubmit)}
              disabled={isLoading}
              className={cn(
                "aero:h-10 aero:gap-2 aero:px-4",
                isLoading
                  ? "aero:bg-neutral-400 aero:cursor-not-allowed"
                  : "aero:bg-[#2F4F4F] hover:aero:bg-[#1F3F3F]"
              )}
            >
              {isLoading ? (
                <>
                  <span className="aero:animate-spin aero:w-4 aero:h-4 aero:border-2 aero:border-white aero:border-t-transparent aero:rounded-full" />
                  Creating...
                </>
              ) : (
                <>
                  <Check className="aero:w-5 aero:h-5" />
                  Create
                </>
              )}
            </Button>
          )}

          {showProcessSelection && (
            <Button
              type="button"
              intent="neutral"
              design="outline"
              onClick={handleBackToForm}
              className="aero:h-9 aero:min-w-[120px] aero:px-4"
            >
              Back
            </Button>
          )}

          {showProcessSelection && (
            <Button
              type="button"
              intent="primary"
              onClick={handleBackToForm}
              disabled={selectedProcesses.length === 0}
              className={cn(
                "aero:h-9 aero:min-w-[169px] aero:px-5",
                selectedProcesses.length === 0
                  ? "aero:bg-neutral-300 aero:text-neutral-600 hover:aero:bg-neutral-300"
                  : "aero:bg-[#2F4F4F] hover:aero:bg-[#1F3F3F] aero:text-white"
              )}
            >
              <Check className="aero:w-4 aero:h-4" />
              Save Workflow
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
